import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "@/context/AuthContext";
import TheoryVisualDiagram from "@/components/theory/TheoryVisualDiagram";

// Requests should recover well before axios' 60s default so a dropped
// connection doesn't leave answer/navigation buttons disabled for a full minute.
const REQUEST_TIMEOUT_MS = 25000;

function SourceLink({ title, url, publisher }) {
  if (!url) return null;
  return (
    <p className="text-sm">
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className="underline font-medium"
        data-testid="theory-source-link"
      >
        View official source{title ? `: ${title}` : ""}
      </a>
      {publisher ? <span className="text-muted-foreground"> — {publisher}</span> : null}
    </p>
  );
}

function assertNewPracticeSession(data, previousSessionId) {
  if (data.status === "interrupted" || data.session_interrupted) {
    return "Could not start a new practice session because an interrupted session was returned instead. Please try again.";
  }
  if (!data.question) {
    return "Practice started but no question was available. Check your connection and try again.";
  }
  if (data.session_id === previousSessionId) {
    return "You are still on the interrupted session. A new practice session could not be created.";
  }
  return null;
}

export default function TheorySession() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [selected, setSelected] = useState("");
  const [feedback, setFeedback] = useState(null);
  const [result, setResult] = useState(null);
  const [review, setReview] = useState(null);
  const [confirmSubmit, setConfirmSubmit] = useState(false);
  const [error, setError] = useState("");
  // `checking` (Check answer) is kept separate from `navBusy` (goto/submit/retry)
  // so a slow answer check doesn't visually disable unrelated navigation controls.
  const [checking, setChecking] = useState(false);
  const [navBusy, setNavBusy] = useState(false);
  const [startingPractice, setStartingPractice] = useState(false);
  const [overviewPublication, setOverviewPublication] = useState(null);
  const [visualFailed, setVisualFailed] = useState(false);
  // Synchronous guard against rapid double-clicks landing before React re-renders
  // the `disabled` state — state updates alone aren't fast enough to prevent that race.
  const inFlightRef = useRef(false);
  const loadGenerationRef = useRef(0);

  const load = useCallback(() => {
    const gen = ++loadGenerationRef.current;
    setState(null);
    setResult(null);
    setReview(null);
    setFeedback(null);
    setSelected("");
    setConfirmSubmit(false);
    setError("");
    setOverviewPublication(null);
    inFlightRef.current = false;

    return api
      .get(`/theory/sessions/${sessionId}`, { timeout: REQUEST_TIMEOUT_MS })
      .then((res) => {
        if (gen !== loadGenerationRef.current) return;
        setState(res.data);
        setSelected((res.data.answers || {})[res.data.question?.question_id] || "");
        setFeedback(null);
        if (res.data.status === "completed") setResult(res.data.result);
      })
      .catch((err) => {
        if (gen !== loadGenerationRef.current) return;
        setError(formatApiError(err) || "Could not load your quiz. Please try again.");
      });
  }, [sessionId]);

  useEffect(() => {
    load();
  }, [load]);

  const isMock = state?.mode === "mock";
  const q = state?.question;

  useEffect(() => {
    setVisualFailed(false);
  }, [q?.question_id, q?.visual?.asset_url]);

  const interruptedView = state?.status === "interrupted" || state?.session_interrupted;
  useEffect(() => {
    if (!interruptedView || !sessionId) return;
    if (state?.theory_publication?.practice_available) return;
    api.get("/theory/overview")
      .then((res) => setOverviewPublication(res.data?.theory_publication || null))
      .catch(() => setOverviewPublication(null));
  }, [interruptedView, sessionId, state?.theory_publication?.practice_available]);

  const choose = async (optionId) => {
    if (feedback || !q?.question_id || inFlightRef.current) return;
    setSelected(optionId);
    if (!isMock) return;
    inFlightRef.current = true;
    setError("");
    try {
      await api.post(
        `/theory/sessions/${sessionId}/answer`,
        { question_id: q.question_id, option_id: optionId },
        { timeout: REQUEST_TIMEOUT_MS }
      );
      setState((prev) => {
        if (!prev) return prev;
        const answers = { ...(prev.answers || {}), [q.question_id]: optionId };
        return { ...prev, answers, answered_count: Object.keys(answers).length };
      });
    } catch (err) {
      setError(formatApiError(err) || "Could not save your answer. Please try again.");
      setSelected((state?.answers || {})[q.question_id] || "");
    } finally {
      inFlightRef.current = false;
    }
  };

  const checkAnswer = async () => {
    if (!selected || inFlightRef.current) return;
    inFlightRef.current = true;
    setChecking(true);
    setError("");
    try {
      const { data } = await api.post(
        `/theory/sessions/${sessionId}/answer`,
        { question_id: q.question_id, option_id: selected },
        { timeout: REQUEST_TIMEOUT_MS }
      );
      setFeedback(data.feedback);
    } catch (err) {
      setError(formatApiError(err) || "Could not check your answer. Please try again.");
    } finally {
      setChecking(false);
      inFlightRef.current = false;
    }
  };

  const goto = async (index) => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setNavBusy(true);
    setError("");
    try {
      const { data } = await api.post(`/theory/sessions/${sessionId}/goto`, { index }, { timeout: REQUEST_TIMEOUT_MS });
      setState(data);
      setSelected((data.answers || {})[data.question?.question_id] || "");
      setFeedback(null);
      window.scrollTo({ top: 0 });
    } catch (err) {
      setError(formatApiError(err) || "Could not load that question. Please try again.");
    } finally {
      setNavBusy(false);
      inFlightRef.current = false;
    }
  };

  const submit = async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setNavBusy(true);
    setError("");
    try {
      const { data } = await api.post(`/theory/sessions/${sessionId}/submit`, undefined, { timeout: REQUEST_TIMEOUT_MS });
      setResult(data.result);
      setConfirmSubmit(false);
      window.scrollTo({ top: 0 });
    } catch (err) {
      setError(formatApiError(err) || "Could not submit your answers. Please try again.");
    } finally {
      setNavBusy(false);
      inFlightRef.current = false;
    }
  };

  const loadReview = async () => {
    setError("");
    try {
      const { data } = await api.get(`/theory/sessions/${sessionId}/review`, { timeout: REQUEST_TIMEOUT_MS });
      setReview(data);
    } catch (err) {
      setError(formatApiError(err) || "Could not load the review. Please try again.");
    }
  };

  const retryIncorrect = async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setNavBusy(true);
    setError("");
    try {
      await api.post(`/theory/sessions/${sessionId}/submit`, undefined, { timeout: REQUEST_TIMEOUT_MS }).catch(() => {});
      const { data } = await api.post(
        "/theory/practice/retry-incorrect",
        { session_id: sessionId },
        { timeout: REQUEST_TIMEOUT_MS }
      );
      window.location.assign(`#/theory/session/${data.session_id}`);
      window.location.reload();
    } catch (err) {
      setError(formatApiError(err) || "Could not start a retry session. Please try again.");
      setNavBusy(false);
      inFlightRef.current = false;
    }
  };

  if (error && !state) return <p role="alert" className="text-destructive" data-testid="theory-session-error">{error}</p>;
  if (!state) return <p data-testid="theory-session-loading">Loading your quiz…</p>;

  const interrupted = state.status === "interrupted" || state.session_interrupted;
  const interruptionMessage =
    state.interruption?.message ||
    "This session includes questions that are no longer available while publication is reviewed.";

  const startNewVerifiedPractice = async () => {
    if (inFlightRef.current || startingPractice) return;
    inFlightRef.current = true;
    setStartingPractice(true);
    setError("");
    try {
      const { data } = await api.post("/theory/practice/start", { category: null, with_visuals: false }, { timeout: REQUEST_TIMEOUT_MS });
      const block = assertNewPracticeSession(data, sessionId);
      if (block) {
        setError(block);
        return;
      }
      navigate(`/theory/session/${data.session_id}`, { replace: true });
    } catch (err) {
      setError(formatApiError(err) || "Could not start a new practice session. Please try again.");
    } finally {
      setStartingPractice(false);
      inFlightRef.current = false;
    }
  };

  if (interrupted && !result) {
    const pub = state.theory_publication || overviewPublication || {};
    const practiceOpen = Boolean(pub.practice_available && !pub.maintenance);
    return (
      <div className="space-y-6" data-testid="theory-session-interrupted">
        <nav><Link to="/theory" className="btn-outline" data-testid="session-back-to-theory">← Back to Theory Test Preparation</Link></nav>
        <section className="card space-y-3 max-w-2xl border-amber-200 bg-amber-50" role="alert">
          <h1 className="text-xl font-bold text-primary">Session interrupted</h1>
          <p data-testid="theory-interruption-message">{interruptionMessage}</p>
          <p className="text-sm text-muted-foreground">
            This session cannot be resumed with substituted questions. Answers you saved here are preserved for your records.
            Completed sessions and scores are unchanged.
          </p>
          {practiceOpen ? (
            <p className="text-sm text-muted-foreground" data-testid="theory-interruption-reopen-hint">
              Verified practice ({pub.verified_publication_count ?? "35"} questions) is available now. Start a new session to continue learning.
            </p>
          ) : (
            <p className="text-sm text-muted-foreground" data-testid="theory-interruption-maintenance-hint">
              New practice will be available when verified publication reopens.
            </p>
          )}
          <p className="text-sm text-muted-foreground" data-testid="theory-interruption-progress">
            Answered in this session before interruption: {state.answered_count} of {state.total}
          </p>
          {practiceOpen && (
            <button
              type="button"
              className="btn-primary min-h-11"
              onClick={startNewVerifiedPractice}
              disabled={startingPractice}
              data-testid="theory-start-new-practice"
            >
              {startingPractice ? "Starting…" : "Start new practice"}
            </button>
          )}
        </section>
        {error && <p role="alert" className="text-destructive" data-testid="theory-inline-error">{error}</p>}
      </div>
    );
  }

  const sessionResult = result ?? (state.status === "completed" ? state.result : null);
  const progress = state.total ? Math.round(100 * (isMock ? state.answered_count : (state.current_index + (feedback ? 1 : 0))) / state.total) : 0;

  if (review) {
    return (
      <div className="space-y-6" data-testid="theory-review">
        <nav><Link to="/theory" className="btn-outline" data-testid="review-back-to-theory">← Back to Theory Test Preparation</Link></nav>
        <h1 className="text-4xl font-bold text-primary">Answer review</h1>
        {review.items.map((item, i) => (
          <section
            key={item.question_id}
            className={`quiz-question-card space-y-2 ${item.correct ? "quiz-result-correct" : "quiz-result-incorrect"}`}
            data-testid={`review-item-${item.question_id}`}
          >
            <p className="font-medium">{i + 1}. {item.stem}</p>
            <p className="text-sm font-semibold">{item.correct ? "✓ Correct" : "✗ Incorrect"}</p>
            {!item.correct && <p className="text-sm"><span className="font-semibold">Your answer: </span>{item.chosen_option_text || "Not answered"}</p>}
            <p className="text-sm"><span className="font-semibold">Correct answer: </span>{item.correct_option_text}</p>
            {item.explanation && <p className="text-sm"><span className="font-semibold">Why: </span>{item.explanation}</p>}
            <SourceLink title={item.official_source_title} url={item.official_source_url} publisher={item.publisher} />
          </section>
        ))}
      </div>
    );
  }

  if (sessionResult) {
    return (
      <div className="space-y-6" data-testid="theory-result">
        <nav><Link to="/theory" className="btn-outline" data-testid="result-back-to-theory">← Back to Theory Test Preparation</Link></nav>
        <section className="card space-y-4 max-w-2xl" data-testid="theory-result-card">
          {isMock ? (
            <>
              <p
                className={`text-2xl font-bold inline-block px-4 py-2 rounded ${sessionResult.passed ? "status-success" : "status-error"}`}
                data-testid={sessionResult.passed ? "mock-result-pass" : "mock-result-fail"}
              >
                {sessionResult.passed ? "PASS" : "NOT YET PASSED"}
              </p>
              <p className="text-base md:text-lg" data-testid="mock-result-score">Score: {sessionResult.score_correct} / {sessionResult.total}</p>
              <p data-testid="mock-result-threshold">Required: {sessionResult.threshold_text}</p>
              <p data-testid="mock-result-counts">Correct: {sessionResult.score_correct} · Incorrect: {sessionResult.score_incorrect}</p>
              <p className="text-sm text-muted-foreground">
                This is a practice mock test — it is not an official NZTA learner licence test result.
              </p>
            </>
          ) : (
            <>
              <p className="text-2xl font-bold" data-testid="practice-result-heading">Practice session complete</p>
              <p data-testid="practice-result-score">Correct: {sessionResult.score_correct} / {sessionResult.total} · Incorrect: {sessionResult.score_incorrect}</p>
            </>
          )}
          <div className="flex flex-wrap gap-3">
            <button type="button" className="btn-primary" onClick={loadReview} data-testid="review-answers-button">Review answers</button>
            {!isMock && sessionResult.score_incorrect > 0 && (
              <button type="button" className="btn-outline" onClick={retryIncorrect} disabled={navBusy} data-testid="retry-incorrect-button">
                {navBusy ? "Starting…" : "Retry incorrect questions"}
              </button>
            )}
            <Link to="/theory" className="btn-outline" data-testid="result-new-session">
              {isMock ? "Start another mock" : "Back to Theory Test Preparation"}
            </Link>
          </div>
          {error && (
            <p role="alert" className="text-destructive" data-testid="theory-inline-error">
              {error}
            </p>
          )}
        </section>
      </div>
    );
  }

  if (!q) {
    return (
      <div className="space-y-4" data-testid="theory-session-no-question">
        <nav><Link to="/theory" className="btn-outline" data-testid="session-back-to-theory">← Back to Theory Test Preparation</Link></nav>
        <p role="alert" className="text-muted-foreground">No question is available for this step.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="theory-session">
      <nav><Link to="/theory" className="btn-outline" data-testid="session-back-to-theory">← Back to Theory Test Preparation</Link></nav>

      <header className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm font-semibold" data-testid="theory-mode-label">
            {isMock ? "Practice Mock Test" : `Practice Mode${state.category ? ` — ${state.category}` : ""}`}
          </p>
          <p className="text-sm font-medium" data-testid="theory-question-counter">
            Question {state.current_index + 1} of {state.total}
          </p>
        </div>
        <div
          className="progress-track"
          role="progressbar"
          aria-valuenow={progress}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Quiz progress"
          data-testid="theory-progress"
        >
          <div className="progress-fill" style={{ width: `${progress}%` }} />
        </div>
      </header>

      <section className="quiz-question-card space-y-4" data-testid="theory-question-card">
        {q.visual?.has_visual && !visualFailed ? (
          q.visual.asset_url ? (
            <figure className="theory-visual-asset" data-testid="theory-visual-asset">
              <img
                src={q.visual.asset_url}
                alt={q.visual.alt || "Theory road diagram"}
                className="w-full max-w-lg mx-auto max-h-[min(52vh,420px)] object-contain rounded-md border border-border bg-white"
                loading="lazy"
                decoding="async"
                onError={() => setVisualFailed(true)}
              />
            </figure>
          ) : q.visual.visual_kind ? (
            <TheoryVisualDiagram kind={q.visual.visual_kind} alt={q.visual.alt} />
          ) : null
        ) : null}
        <h1 className="text-base md:text-lg font-semibold" data-testid="theory-question-stem">{q.stem}</h1>
        <div className="space-y-2" role="radiogroup" aria-label="Answer options">
          {q.options.map((opt) => {
            const isChosen = selected === opt.id;
            const isCorrectOpt = feedback && feedback.correct_option_id === opt.id;
            const isWrongChosen = feedback && !feedback.correct && isChosen;
            // Presentation-only: bank text may already include "A. …"; avoid "A. A. …"
            const label = String(opt.id || "").trim();
            const raw = String(opt.text || "");
            const displayText = label
              ? raw.replace(new RegExp(`^\\s*${label.replace(/[.*+?^${}()|[\\]\\\\]/g, "\\$&")}\\.\\s*`, "i"), "")
              : raw;
            return (
              <button
                key={opt.id}
                type="button"
                role="radio"
                aria-checked={isChosen}
                disabled={Boolean(feedback)}
                onClick={() => choose(opt.id)}
                className={`quiz-option w-full text-left ${isChosen ? "quiz-option-checked" : ""} ${isWrongChosen ? "quiz-option-wrong" : ""} ${isCorrectOpt ? "quiz-option-right" : ""}`}
                data-testid={`theory-option-${q.question_id}-${opt.id}`}
              >
                <span className="font-semibold mr-1">{label}.</span>
                <span>
                  {displayText}
                  {isWrongChosen ? " (Your answer)" : ""}
                  {isCorrectOpt && feedback ? " (Correct answer)" : ""}
                </span>
              </button>
            );
          })}
        </div>

        {feedback && (
          <div
            className={`space-y-2 text-sm rounded p-3 ${feedback.correct ? "quiz-result-correct" : "quiz-result-incorrect"}`}
            role="status"
            data-testid={feedback.correct ? "theory-feedback-correct" : "theory-feedback-incorrect"}
          >
            <p className="font-semibold">{feedback.correct ? "✓ Correct" : "✗ Incorrect"}</p>
            {!feedback.correct && <p><span className="font-semibold">Your answer: </span>{feedback.chosen_option_text}</p>}
            <p><span className="font-semibold">Correct answer: </span>{feedback.correct_option_text}</p>
            {feedback.explanation && <p><span className="font-semibold">Why: </span>{feedback.explanation}</p>}
            <SourceLink title={feedback.official_source_title} url={feedback.official_source_url} publisher={feedback.publisher} />
          </div>
        )}

        <div className="flex flex-wrap gap-3 pt-2">
          {!isMock && !feedback && (
            <button type="button" className="btn-primary" disabled={!selected || checking} onClick={checkAnswer} data-testid="theory-check-answer">
              {checking ? "Checking…" : "Check answer"}
            </button>
          )}
          {isMock && state.current_index > 0 && (
            <button type="button" className="btn-outline" disabled={navBusy} onClick={() => goto(state.current_index - 1)} data-testid="mock-prev-question">
              Previous
            </button>
          )}
          {state.current_index < state.total - 1 ? (
            (!isMock && !feedback) ? null : (
              <button type="button" className="btn-primary" disabled={navBusy} onClick={() => goto(state.current_index + 1)} data-testid="theory-next-question">
                {navBusy ? "Loading…" : "Next question"}
              </button>
            )
          ) : (
            (!isMock && !feedback) ? null : (
              <button type="button" className="btn-accent" disabled={navBusy} onClick={() => setConfirmSubmit(true)} data-testid="theory-finish-button">
                {isMock ? "Finish mock test" : "Finish practice"}
              </button>
            )
          )}
        </div>
        {isMock && (
          <p className="text-sm text-muted-foreground" data-testid="mock-answered-count">
            Answered: {state.answered_count} of {state.total}
          </p>
        )}
      </section>

      {confirmSubmit && (
        <section className="card space-y-3 max-w-xl" role="alertdialog" aria-label="Confirm submission" data-testid="theory-confirm-submit">
          <p className="font-medium">
            {isMock
              ? `Submit your mock test now? You have answered ${state.answered_count} of ${state.total} questions. Unanswered questions count as incorrect.`
              : "Finish this practice session?"}
          </p>
          <div className="flex flex-wrap gap-3">
            <button type="button" className="btn-primary" disabled={navBusy} onClick={submit} data-testid="theory-confirm-submit-button">
              {navBusy ? "Submitting…" : "Submit final answers"}
            </button>
            <button type="button" className="btn-outline" onClick={() => setConfirmSubmit(false)} data-testid="theory-cancel-submit">
              Keep answering
            </button>
          </div>
        </section>
      )}
      {error && <p role="alert" className="text-destructive" data-testid="theory-inline-error">{error}</p>}
    </div>
  );
}
